import { apiRequest } from './api';
import { Client, ApiResponse, PaginationParams, Contract } from '../types';


// Função para converter dados do backend (PT-BR) para frontend
const convertClientFromBackend = (backendClient: any): Client => {
  return {
    id: backendClient.id,
    escritorio_id: backendClient.escritorio_id,
    nome: backendClient.nome,
    cpf_cnpj: backendClient.cpf_cnpj,
    tipo_pessoa: backendClient.tipo_pessoa,
    data_nascimento: backendClient.data_nascimento,
    rg_ie: backendClient.rg_ie,
    contribuinte_icms: backendClient.contribuinte_icms,
    nome_mae: backendClient.nome_mae,
    nome_pai: backendClient.nome_pai,
    endereco: backendClient.endereco,
    bairro: backendClient.bairro,
    cidade: backendClient.cidade,
    estado: backendClient.estado,
    codigo_municipio: backendClient.codigo_municipio,
    cep: backendClient.cep,
    numero: backendClient.numero,
    complemento: backendClient.complemento,
    referencia: backendClient.referencia,
    status: backendClient.status,
    observacoes: backendClient.observacoes,
    central_password: backendClient.central_password,
    permitir_acesso_central: backendClient.permitir_acesso_central,
    indicado_por: backendClient.indicado_por,
    vendedor_id: backendClient.vendedor_id,
    createdAt: backendClient.createdAt,
    updatedAt: backendClient.updatedAt,
    // Incluir contratos se existirem
    contratos: backendClient.contratos || [],
    tipo_cliente: backendClient.tipo_cliente,
    como_conheceu: backendClient.como_conheceu,
    tipo_moradia: backendClient.tipo_moradia,
    data_desativacao: backendClient.data_desativacao,
    motivo_desativacao: backendClient.motivo_desativacao,
    desativado_por: backendClient.desativado_por,
    usuario_desativou: backendClient.usuario_desativou,
    vendedor: backendClient.vendedor,
    escritorio: backendClient.escritorio,
    cliente_indicador: backendClient.cliente_indicador,
  };
};

// Função para converter dados do frontend para backend
const convertClientToBackend = (frontendClient: Partial<Client>): any => {
  return {
    escritorio_id: frontendClient.escritorio_id,
    nome: frontendClient.nome,
    cpf_cnpj: frontendClient.cpf_cnpj,
    tipo_pessoa: frontendClient.tipo_pessoa,
    data_nascimento: frontendClient.data_nascimento,
    // Converter undefined para null para rg_ie (banco de dados espera null)
    rg_ie: frontendClient.rg_ie === undefined ? null : frontendClient.rg_ie,
    contribuinte_icms: frontendClient.contribuinte_icms,
    nome_mae: frontendClient.nome_mae,
    nome_pai: frontendClient.nome_pai,
    endereco: frontendClient.endereco,
    bairro: frontendClient.bairro,
    cidade: frontendClient.cidade,
    estado: frontendClient.estado,
    codigo_municipio: frontendClient.codigo_municipio,
    cep: frontendClient.cep,
    numero: frontendClient.numero,
    complemento: frontendClient.complemento,
    referencia: frontendClient.referencia,
    status: frontendClient.status,
    observacoes: frontendClient.observacoes,
    central_password: frontendClient.central_password,
    permitir_acesso_central: frontendClient.permitir_acesso_central,
    indicado_por: frontendClient.indicado_por,
    vendedor_id: frontendClient.vendedor_id,
    tipo_cliente: frontendClient.tipo_cliente,
    como_conheceu: frontendClient.como_conheceu,
    tipo_moradia: frontendClient.tipo_moradia
  };
};

export const clientService = {
  // Listar todos os clientes
  getAll: async (params?: PaginationParams & { status?: string }): Promise<ApiResponse<Client[]> & { stats?: any }> => {
    // Adicionar parâmetro para incluir contratos
    const queryParams = {
      ...params,
      include: 'contratos'
    };
    const response = await apiRequest.get<Client[]>('/clients', { params: queryParams });
    // Converter dados do backend
    if (response.data) {
      response.data = response.data.map(convertClientFromBackend);
    }
    return response;
  },

  // Obter cliente por ID
  getById: async (id: number): Promise<Client> => {
    const response = await apiRequest.get<any>(`/clients/${id}`);
    return convertClientFromBackend(response.data!);
  },

  // Criar novo cliente
  create: async (client: Partial<Client>): Promise<Client> => {
    const backendData = convertClientToBackend(client);
    const response = await apiRequest.post<any>('/clients', backendData);
    return convertClientFromBackend(response.data!);
  },

  // Atualizar cliente
  update: async (id: number, client: Partial<Client>): Promise<Client> => {
    const backendData = convertClientToBackend(client);
    const response = await apiRequest.put<any>(`/clients/${id}`, backendData);
    return convertClientFromBackend(response.data!);
  },

  // Excluir cliente
  delete: async (id: number): Promise<void> => {
    await apiRequest.delete(`/clients/${id}`);
  },

  // Alterar status do cliente
  changeStatus: async (id: number, status: string): Promise<Client> => {
    const response = await apiRequest.patch<any>(`/clients/${id}/status`, { status });
    return convertClientFromBackend(response.data!);
  },


  // Buscar clientes por status
  getByStatus: async (status: string, params?: PaginationParams): Promise<ApiResponse<Client[]>> => {
    const response = await apiRequest.get<Client[]>(`/clients/status/${status}`, { params });

    // Converter dados do backend
    if (response.data) {
      response.data = response.data.map(convertClientFromBackend);
    }

    return response;
  },

  // Obter clientes ativos com contratos
  getClientesAtivos: async (): Promise<ApiResponse<Client[]>> => {
    const response = await apiRequest.get<Client[]>('/clients/ativos');

    // Converter dados do backend
    if (response.data) {
      response.data = response.data.map(convertClientFromBackend);
    }

    return response;
  },

  // Obter clientes inativos
  getClientesInativos: async (): Promise<ApiResponse<Client[]>> => {
    const response = await apiRequest.get<Client[]>('/clients/inativos');

    // Converter dados do backend
    if (response.data) {
      response.data = response.data.map(convertClientFromBackend);
    }

    return response;
  },

  // Obter clientes sem contrato
  getClientesSemContrato: async (): Promise<ApiResponse<Client[]>> => {
    const response = await apiRequest.get<Client[]>('/clients/sem-contrato');

    // Converter dados do backend
    if (response.data) {
      response.data = response.data.map(convertClientFromBackend);
    }

    return response;
  },

  // Buscar aniversariantes do mês
  getAniversariantesMes: async (): Promise<ApiResponse<Client[]>> => {
    const response = await apiRequest.get<Client[]>('/clients/getAniversariantes');
    if (response.data) {
      response.data = response.data.map(convertClientFromBackend);
    }
    return response;
  },

  // Buscar novos clientes do mês
  getNovosClientesMes: async (): Promise<ApiResponse<Client[]>> => {
    const response = await apiRequest.get<Client[]>('/clients/novos-clientes-mes');
    if (response.data) {
      response.data = response.data.map(convertClientFromBackend);
    }
    return response;
  },

  /** Lista bairros distintos de clientes já cadastrados, filtrados pela cidade informada. */
  getBairrosByCidade: async (cidade: string): Promise<string[]> => {
    const c = (cidade || '').trim();
    if (!c) return [];
    // apiRequest retorna response.data (interceptor); então body já é { success, data }
    const body = await apiRequest.get<{ success?: boolean; data?: string[] }>('/clients/bairros', {
      params: { cidade: c },
    });
    if (body?.success === true && Array.isArray(body.data)) return body.data;
    return [];
  },

  // Buscar cliente por CPF/CNPJ
  getByCpfCnpj: async (cpfCnpj: string): Promise<Client | null> => {
    try {
      const response = await apiRequest.post<any>(`/clients/cpf-cnpj`, { cpf_cnpj: cpfCnpj });
      if (response.data) {
        return convertClientFromBackend(response.data);
      }
      return null;
    } catch (err: any) {
      // Se não encontrar, retorna null
      if (err.response?.status === 404) {
        return null;
      }
      throw err;
    }
  },

  // Desativar cliente
  desativar: async (id: number, motivo: string, confirmarPendencias: boolean = false): Promise<any> => {
    return await apiRequest.post(`/clients/${id}/desativar`, { motivo, confirmar_pendencias: confirmarPendencias });
  },

  // Verificar pendências do cliente
  checkPendencias: async (id: number): Promise<any> => {
    return await apiRequest.get(`/clients/${id}/check-pendencias`);
  },

  // Ativar cliente
  ativar: async (id: number): Promise<void> => {
    await apiRequest.post(`/clients/${id}/ativar`);
  },

}; 